// Copyright (c) Docugami, Inc. All rights reserved.

import { Auth0ContextInterface, GetTokenSilentlyOptions, useAuth0, User } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react"
import { AppState } from "store";
import jwt from 'jwt-decode'
import { setUserRolesPerOrg } from "store/authentication/actions";
import { useAppDispatch, useAppSelector } from "./redux";
import { createSelector } from "@reduxjs/toolkit";
import { Config } from "config";
import axios from "axios";
import { useGetUserProfileQuery } from "store/user/auth0UserSlice";


const selectIsAdminRole = createSelector(
    [
        (state: AppState): string[] | undefined => !state.organizations.currentOrganization?.id || !state.authentication.userRolesPerOrg ? undefined : state.authentication.userRolesPerOrg[state.organizations.currentOrganization.id] ?? []
    ],
    (roles) => roles === undefined ? undefined : roles.findIndex(x => x === 'org-admin') !== -1
);

export interface IDgAuth extends Auth0ContextInterface<User> {
    user: User | undefined;
    isOrganizationAdmin: boolean | undefined;
    requestPasswordReset: () => Promise<string | undefined>;
    setMultiFactorAuth: (useMfa: boolean) => Promise<boolean>;
    isUserNamePassword: boolean | undefined;
    isMultiFactorAuth: boolean | undefined;
    hasOrgClaims: boolean | undefined;
}

export const useDgAuth = (): IDgAuth => {
    const auth0Context = useAuth0();
    const { getAccessTokenSilently } = auth0Context;
    const isOrganizationAdmin = useAppSelector(state => selectIsAdminRole(state));
    const hasOrgClaims = useAppSelector((state: AppState) => !state.authentication.userRolesPerOrg ? undefined : Object.keys(state.authentication.userRolesPerOrg).length > 0);
    const dispatch = useAppDispatch();
    const [isAuth0User, setIsAuth0User] = useState<boolean | undefined>(undefined);
    const [isMultiFactorAuth, setIsMultiFactorAuth] = useState<boolean | undefined>(undefined);
    const { data: userProfile } = useGetUserProfileQuery(undefined, { skip: !auth0Context.isAuthenticated });

    const updateFromIdTokenClaims = useCallback(async (): Promise<void> => {
        try{
            const idClaims = await auth0Context.getIdTokenClaims();
            if (!idClaims) {
                throw Error;
            }

            setIsAuth0User(idClaims.sub.startsWith("auth0|"));
            const user_mfa = idClaims['https://docugami.com/user/mfa'];
            setIsMultiFactorAuth(user_mfa === true);
        } catch {
            setIsAuth0User(undefined);
            setIsMultiFactorAuth(undefined);
        }
    }, [auth0Context])

    const dgGetAccessTokenSilently = useCallback(async (opts?: GetTokenSilentlyOptions): Promise<any> => {
        const accessToken = await getAccessTokenSilently(opts);
        const decodedToken = jwt<any>(accessToken);
        const userRolesPerOrg: { [orgId: string]: string[] } = {}
        Object.entries(decodedToken).forEach(([key, value], ) => {
            if (key.startsWith("https://docugami.com/organization/") && key.endsWith("/roles")) {
                const orgId = key.substring("https://docugami.com/organization/".length, key.length - "/roles".length);
                // @ts-ignore
                userRolesPerOrg[orgId] = value;
            }
        });
        dispatch(setUserRolesPerOrg(userRolesPerOrg));
        await updateFromIdTokenClaims();

        return accessToken;
    },
    [getAccessTokenSilently, dispatch, updateFromIdTokenClaims])

    const requestPasswordReset = useCallback(async (): Promise<string | undefined> => {
        if (!isAuth0User || !auth0Context.user?.email) {
            return undefined;
        }

        const data = {
            client_id: Config.auth0.clientId,
            email: auth0Context.user.email,
            connection: 'user-name-password'
        }

        try {
            const response = await axios.post(`https://${Config.auth0.domain}/dbconnections/change_password`, data);
            return response.data;
        } catch {
            return "Sorry, something went wrong."
        }
    },
    [auth0Context.user, isAuth0User])

    const setMultiFactorAuth = useCallback(async (useMfa: boolean): Promise<boolean> => {
        if (!isAuth0User || !auth0Context.user?.email) {
            return false;
        }

        try {
            const url = Config.organizationApiUrl + "/mfa/user";
            let response;
            if (useMfa) {
                response = await axios.post(url);
            } else {
                response = await axios.delete(url);
            }
            return  response.status === 200;
        } catch {
            return false;
        }
    },
    [auth0Context.user, isAuth0User])

    const user: User | undefined = useMemo(() => {
        if (!auth0Context.user) {
            return undefined;
        }

        let name: string;
        if (!!userProfile?.given_name && !!userProfile?.family_name) {
            name = `${userProfile.given_name} ${userProfile.family_name}`;
        } else if (auth0Context.user?.name?.trim().toUpperCase() !== auth0Context.user?.email?.trim().toUpperCase()) {
            name = auth0Context.user?.name ?? '';
        } else if (!!auth0Context.user?.given_name && !!auth0Context.user?.family_name) {
            name = `${auth0Context.user?.given_name} ${auth0Context.user?.family_name}`;
        } else {
            name = auth0Context.user?.name ?? '';
        }

        return {
            ...auth0Context.user,
            name: name,
            given_name: userProfile?.given_name ?? auth0Context.user?.given_name ?? '',
            family_name: userProfile?.family_name ?? auth0Context.user?.family_name ?? '',
        };
    }, [auth0Context.user, userProfile?.given_name, userProfile?.family_name])

    useEffect(() => {
        updateFromIdTokenClaims();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

    return {
        ...auth0Context,
        user: user,
        getAccessTokenSilently: dgGetAccessTokenSilently,
        isOrganizationAdmin: isOrganizationAdmin,
        requestPasswordReset: requestPasswordReset,
        isUserNamePassword: isAuth0User,
        isMultiFactorAuth: isMultiFactorAuth,
        setMultiFactorAuth: setMultiFactorAuth,
        hasOrgClaims: hasOrgClaims
    }
}
